import { PlusIcon } from '@shopify/polaris-icons';
import { Page, Modal, TextField, Select } from '../components/forms';
import { useEffect, useState } from 'react';
import {
  Layout,
  Card,
  Text,
  BlockStack,
  InlineStack,
  Button,
  Badge,
  DataTable,
  FormLayout,
  Checkbox,
  Banner,
  Spinner,
  SkeletonBodyText,
} from '@shopify/polaris';
import { useApi } from '../hooks/useApi';

interface VendorRule {
  id: number;
  vendor: string;
  warehouse_id: number;
  warehouse_name?: string;
  default_location_id: number | null;
  location_code?: string;
  priority: number;
  is_active: number;
  created_at: string;
}

interface Warehouse {
  id: number;
  name: string;
}

interface Location {
  id: number;
  code: string;
  warehouse_id: number;
}

export default function VendorRules() {
  const { fetchApi, error } = useApi();
  const [rules, setRules] = useState<VendorRule[]>([]);
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalActive, setModalActive] = useState(false);
  const [editingRule, setEditingRule] = useState<VendorRule | null>(null);
  const [saving, setSaving] = useState(false);
  const [sortOrder, setSortOrder] = useState('priority-asc');

  // Form state
  const [formVendor, setFormVendor] = useState('');
  const [formWarehouseId, setFormWarehouseId] = useState('');
  const [formLocationId, setFormLocationId] = useState('');
  const [formPriority, setFormPriority] = useState('0');
  const [formActive, setFormActive] = useState(true);
  const priorityValid = /^\d+$/.test(formPriority) && Number.isSafeInteger(Number(formPriority));
  const sortedRules = [...rules].sort((a, b) => {
    const primary = sortOrder === 'vendor' ? a.vendor.localeCompare(b.vendor)
      : sortOrder === 'warehouse' ? (a.warehouse_name || '').localeCompare(b.warehouse_name || '')
      : (a.priority - b.priority) * (sortOrder === 'priority-desc' ? -1 : 1);
    return primary || a.vendor.localeCompare(b.vendor) || a.id - b.id;
  });

  // Unique vendors from products
  const [vendors, setVendors] = useState<string[]>([]);

  useEffect(() => {
    loadData();
  }, []);

  async function loadData() {
    setLoading(true);
    try {
      const [rulesData, warehousesData] = await Promise.all([
        fetchApi<{ rules: VendorRule[]; vendors: string[] }>('/products/vendor-rules'),
        fetchApi<{ warehouses: Warehouse[] }>('/inventory/warehouses'),
      ]);

      if (rulesData?.rules) {
        setRules(rulesData.rules);
      }
      if (rulesData?.vendors) {
        setVendors(rulesData.vendors);
      }
      if (warehousesData?.warehouses) {
        setWarehouses(warehousesData.warehouses);
      }
    } finally {
      setLoading(false);
    }
  }

  async function loadLocationsForWarehouse(warehouseId: string) {
    if (!warehouseId) {
      setLocations([]);
      return;
    }
    const data = await fetchApi<{ warehouse: Warehouse; locations: Location[] }>(
      `/inventory/warehouses/${warehouseId}`
    );
    if (data?.locations) {
      setLocations(data.locations);
    }
  }

  const openNewRuleModal = () => {
    setEditingRule(null);
    setFormVendor('');
    setFormWarehouseId('');
    setFormLocationId('');
    setFormPriority('0');
    setFormActive(true);
    setLocations([]);
    setModalActive(true);
  };

  const openEditRuleModal = async (rule: VendorRule) => {
    setEditingRule(rule);
    setFormVendor(rule.vendor);
    setFormWarehouseId(String(rule.warehouse_id));
    setFormLocationId(rule.default_location_id == null ? '' : String(rule.default_location_id));
    setFormPriority(String(rule.priority));
    setFormActive(!!rule.is_active);
    await loadLocationsForWarehouse(String(rule.warehouse_id));
    setModalActive(true);
  };

  const handleWarehouseChange = async (warehouseId: string) => {
    setFormWarehouseId(warehouseId);
    setFormLocationId('');
    await loadLocationsForWarehouse(warehouseId);
  };

  const handleSaveRule = async () => {
    if (!formVendor || !formWarehouseId || !priorityValid) return;

    setSaving(true);
    try {
      const ruleData = {
        vendor: formVendor,
        warehouse_id: formWarehouseId,
        default_location_id: formLocationId || null,
        priority: Number(formPriority),
        is_active: formActive,
      };

      const result = await fetchApi<{ success: boolean }>(editingRule ? `/products/vendor-rules/${editingRule.id}` : '/products/vendor-rules', {
        method: editingRule ? 'PUT' : 'POST',
        body: JSON.stringify(ruleData),
      });
      if (!result?.success) return;

      setModalActive(false);
      loadData();
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteRule = async (ruleId: number) => {
    if (confirm('Are you sure you want to delete this vendor rule?')) {
      await fetchApi(`/products/vendor-rules/${ruleId}`, { method: 'DELETE' });
      loadData();
    }
  };

  const warehouseOptions = [
    { label: 'Select a warehouse', value: '' },
    ...warehouses.map((wh) => ({ label: wh.name, value: String(wh.id) })),
  ];

  const locationOptions = [
    { label: 'No default location', value: '' },
    ...locations.map((loc) => ({ label: loc.code, value: String(loc.id) })),
  ];

  const vendorOptions = [
    { label: 'Select or enter vendor', value: '' },
    ...vendors.map((v) => ({ label: v, value: v })),
  ];

  return (
    <Page
      title="Vendor Warehouse Rules"
      primaryAction={{
        icon: PlusIcon, content: 'Add Rule',
        onAction: openNewRuleModal,
      }}
      backAction={{ content: 'Products', url: '/products' }}
    >
      <Layout>
        {error && (
          <Layout.Section>
            <Banner tone="critical">Error: {error}</Banner>
          </Layout.Section>
        )}

        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <Text as="p" tone="subdued">
                Automatically assign products to warehouses based on their vendor.
                When a product is synced from Shopify, if its vendor matches a rule,
                the product will be assigned to the specified warehouse and location.
              </Text>

              <Select label="Sort rules" value={sortOrder} onChange={setSortOrder} options={[
                { label: 'Priority: lowest number first', value: 'priority-asc' },
                { label: 'Priority: highest number first', value: 'priority-desc' },
                { label: 'Vendor: A to Z', value: 'vendor' },
                { label: 'Warehouse: A to Z', value: 'warehouse' },
              ]} />
              <Text as="p" tone="subdued">Rules are sorted by priority by default. Lower numbers run first, starting at 0.</Text>
              {loading ? (
                <BlockStack gap="400">
                  <InlineStack align="center" gap="200">
                    <Spinner size="small" />
                    <Text as="p" tone="subdued">Loading vendor rules...</Text>
                  </InlineStack>
                  <SkeletonBodyText lines={3} />
                </BlockStack>
              ) : rules.length > 0 ? (
                <DataTable
                  columnContentTypes={['text', 'text', 'text', 'numeric', 'text', 'text']}
                  headings={['Vendor', 'Warehouse', 'Default Location', 'Priority', 'Status', 'Actions']}
                  rows={sortedRules.map((rule) => [
                    rule.vendor,
                    rule.warehouse_name || `Warehouse #${rule.warehouse_id}`,
                    rule.location_code || '-',
                    rule.priority.toString(),
                    rule.is_active ? (
                      <Badge tone="success">Active</Badge>
                    ) : (
                      <Badge>Inactive</Badge>
                    ),
                    <InlineStack gap="200">
                      <Button size="slim" onClick={() => openEditRuleModal(rule)}>
                        Edit
                      </Button>
                      <Button
                        size="slim"
                        tone="critical"
                        onClick={() => handleDeleteRule(rule.id)}
                      >
                        Delete
                      </Button>
                    </InlineStack>,
                  ])}
                />
              ) : (
                <BlockStack gap="400" inlineAlign="center">
                  <Text as="p" tone="subdued">
                    No vendor rules configured. Products will need to be manually assigned to warehouses.
                  </Text>
                  <Button icon={PlusIcon} onClick={openNewRuleModal}>Add Your First Rule</Button>
                </BlockStack>
              )}
            </BlockStack>
          </Card>
        </Layout.Section>

        <Layout.Section variant="oneThird">
          <Card>
            <BlockStack gap="400">
              <Text as="h2" variant="headingMd">How It Works</Text>
              <BlockStack gap="200">
                <Text as="p" variant="bodyMd">
                  <strong>1. Match by Vendor:</strong> When products sync from Shopify, their vendor field is checked against these rules.
                </Text>
                <Text as="p" variant="bodyMd">
                  <strong>2. Auto-Assign:</strong> Matching products are automatically assigned to the specified warehouse.
                </Text>
                <Text as="p" variant="bodyMd">
                  <strong>3. Default Location:</strong> Optionally set a default bin location for receiving stock.
                </Text>
                <Text as="p" variant="bodyMd">
                  <strong>4. Priority:</strong> Lower numbers = higher priority. If a vendor matches multiple rules, the highest priority wins.
                </Text>
              </BlockStack>
            </BlockStack>
          </Card>
        </Layout.Section>
      </Layout>

      {/* Add/Edit Rule Modal */}
      <Modal
        open={modalActive}
        onClose={() => setModalActive(false)}
        title={editingRule ? 'Edit Vendor Rule' : 'Add Vendor Rule'}
        primaryAction={{
          icon: editingRule ? undefined : PlusIcon, content: 'Save',
          onAction: handleSaveRule,
          loading: saving,
          disabled: !formVendor || !formWarehouseId || !priorityValid,
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: () => setModalActive(false),
          },
        ]}
      >
        <Modal.Section>
          {error && <Banner tone="critical">{error}</Banner>}
          <FormLayout>
            {vendors.length > 0 ? (
              <Select
                label="Vendor"
                options={vendorOptions}
                requiredIndicator value={formVendor}
                onChange={setFormVendor}
                helpText="Select from existing product vendors"
              />
            ) : (
              <TextField
                label="Vendor"
                requiredIndicator value={formVendor}
                onChange={setFormVendor}
                placeholder="Enter vendor name exactly as it appears in Shopify"
                autoComplete="off"
                helpText="Case-sensitive match against product vendor field"
              />
            )}

            <Select
              label="Warehouse"
              options={warehouseOptions}
              requiredIndicator value={formWarehouseId}
              onChange={handleWarehouseChange}
              helpText="Products from this vendor will be assigned to this warehouse"
            />

            {formWarehouseId && locations.length > 0 && (
              <Select
                label="Default Location"
                options={locationOptions}
                value={formLocationId}
                onChange={setFormLocationId}
                helpText="Optional: Preferred bin location for receiving stock"
              />
            )}

            <TextField
              label="Priority"
              type="number"
              min={0}
              step={1}
              value={formPriority}
              onChange={setFormPriority}
              error={!priorityValid ? 'Enter a whole number of 0 or more.' : undefined}
              helpText="Lower number = higher priority (0 is highest)"
              autoComplete="off"
            />

            <Checkbox
              label="Rule is active"
              checked={formActive}
              onChange={setFormActive}
            />
          </FormLayout>
        </Modal.Section>
      </Modal>
    </Page>
  );
}
